import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// reactstrap components
import {
  Table,
  Button,
  Badge,
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Input,
  Label,
  Card,
  CardBody,
} from "reactstrap";

// Local components
import MerchantDetailsModal from './MerchantDetailsModal';
import CreateApplicationModal from '../../CreateApplicationModal';
import UpdateMerchantModal from '../../components/modals/UpdateMerchantModal';
import { formatNumber } from '../../../../utils/formatUtils';

function MerchantTable({ 
  merchants = [], 
  isLoading = false, 
  error = null,
  // API pagination props
  currentPage = 0,
  pageSize = 10,
  totalElements = 0,
  totalPages = 0,
  onPageChange = null,
  onPageSizeChange = null,
  useApiPagination = false,
  onRefresh = () => {},

}) {
  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      
      // Show date and time in a compact format
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };
  // Ensure merchants is always an array
  const safeMerchants = Array.isArray(merchants) ? merchants : [];
  const navigate = useNavigate();
  
  // For client-side pagination (legacy support)
  const [clientCurrentPage, setClientCurrentPage] = useState(1);
  const [clientPageSize, setClientPageSize] = useState(10);
  
  // Sorting (still client-side for display)
  const [sortField, setSortField] = useState('createdDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateAppOpen, setIsCreateAppOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);

  // Reset to page 1 when merchants change (new search) - only for client-side pagination
  React.useEffect(() => {
    if (!useApiPagination) {
      setClientCurrentPage(1);
    }
  }, [safeMerchants.length, useApiPagination]);

  // Pagination logic - choose between API and client-side
  const actualCurrentPage = useApiPagination ? currentPage : clientCurrentPage;
  const actualPageSize = useApiPagination ? pageSize : clientPageSize;
  const actualTotalElements = useApiPagination ? totalElements : safeMerchants.length;
  const actualTotalPages = useApiPagination ? totalPages : Math.ceil(safeMerchants.length / clientPageSize);

  // For client-side pagination, slice the data
  const currentMerchants = useApiPagination 
    ? safeMerchants // API already returns the page data
    : safeMerchants.slice((clientCurrentPage - 1) * clientPageSize, clientCurrentPage * clientPageSize);

  // Display calculations
  const showingFrom = actualTotalElements > 0 
    ? (useApiPagination ? (currentPage * pageSize) + 1 : ((clientCurrentPage - 1) * clientPageSize) + 1)
    : 0;
  const showingTo = useApiPagination 
    ? Math.min((currentPage * pageSize) + safeMerchants.length, actualTotalElements)
    : Math.min(clientCurrentPage * clientPageSize, actualTotalElements);

  // Use 1-based pagination for display
  const displayCurrentPage = useApiPagination ? actualCurrentPage + 1 : actualCurrentPage;
  const displayTotalPages = actualTotalPages;



  // Sorting logic
  const sortedMerchants = React.useMemo(() => {
    return [...currentMerchants].sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      // Special handling for date fields
      if (sortField === 'createdDate' || sortField === 'createdAt' || sortField === 'created_at') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
        
        // For dates: newer dates should appear first (descending order)
        if (sortDirection === 'desc') {
          // Newest first: 2025-08-25 before 2025-08-24
          return bValue - aValue;
        } else {
          // Oldest first: 2025-08-24 before 2025-08-25
          return aValue - bValue;
        }
      }
      
      // Default string comparison for other fields
      if (sortDirection === 'asc') {
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        return bValue.toString().localeCompare(aValue.toString());
      }
    });
  }, [currentMerchants, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChangeInternal = (page) => {
    if (useApiPagination && onPageChange) {
      // API pagination - convert from 1-based to 0-based for API
      onPageChange(page - 1);
    } else {
      // Client-side pagination
      setClientCurrentPage(page);
    }
  };

  const handlePageSizeChangeInternal = (e) => {
    const newPageSize = Number(e.target.value);
    // Validate maximum page size of 100
    const validatedSize = Math.min(Math.max(1, newPageSize), 100);
    
    if (validatedSize !== newPageSize) {
      console.warn(`🚨 Page size ${newPageSize} exceeds maximum (100), using 100 instead`);
    }
    
    if (useApiPagination && onPageSizeChange) {
      // API pagination
      onPageSizeChange(validatedSize);
    } else {
      // Client-side pagination
      setClientPageSize(validatedSize);
      setClientCurrentPage(1);
    }
  };

  const handleView = (merchantId) => {
    const merchant = safeMerchants.find(m => m.id === merchantId);
    if (merchant) {
      setSelectedMerchant(merchant);
      setIsModalOpen(true);
    }
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (isModalOpen) {
      setSelectedMerchant(null);
    }
  };

  const openCreateApplication = (merchantId) => {
    setSelectedMerchant({ id: merchantId });
    setIsCreateAppOpen(true);
  };

  const closeCreateApplication = () => {
    setIsCreateAppOpen(false);
  };

  const openUpdateMerchant = (merchantId) => {
    setSelectedMerchant({ id: merchantId });
    setIsUpdateOpen(true);
  };

  const closeUpdateMerchant = () => {
    setIsUpdateOpen(false);
  };


  const getSortIcon = (field) => {
    if (sortField !== field) return 'ni ni-bold-down';
    return sortDirection === 'asc' ? 'ni ni-bold-up' : 'ni ni-bold-down';
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, displayCurrentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(displayTotalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <Pagination className="justify-content-center">
        <PaginationItem disabled={displayCurrentPage === 1}>
          <PaginationLink onClick={() => handlePageChangeInternal(displayCurrentPage - 1)}>
            <i className="ni ni-bold-left" />
          </PaginationLink>
        </PaginationItem>
        
        {pages.map(page => (
          <PaginationItem key={page} active={page === displayCurrentPage}>
            <PaginationLink onClick={() => handlePageChangeInternal(page)}>
              {page}
            </PaginationLink>
          </PaginationItem>
        ))}
        
        <PaginationItem disabled={displayCurrentPage === displayTotalPages}>
          <PaginationLink onClick={() => handlePageChangeInternal(displayCurrentPage + 1)}>
            <i className="ni ni-bold-right" />
          </PaginationLink>
        </PaginationItem>
      </Pagination>
    );
  };

  return (
    <>
      <style>
        {`
          .text-truncate {
            max-width: 10rem;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        `}
      </style>
      <div>
      {/* Desktop Table View */}
      <div className="d-none d-md-block">
        <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table className="table table-hover" responsive style={{ minWidth: '1100px', whiteSpace: 'nowrap' }}>
          
          <thead className="thead-light">
            <tr>
              <th scope="col" className="border-0" style={{ width: '10%' }}>
                <div className="d-flex align-items-center compact-text">
                  Actions
                </div>
              </th>
              <th scope="col" className="border-0" style={{ width: '20%' }}>
                <div className="d-flex align-items-center compact-text">
                  Business
                </div>
              </th>
              <th scope="col" className="border-0" style={{ width: '16%' }}>
                <div className="d-flex align-items-center compact-text">
                  DBA & Tax ID
                </div>
              </th>
              <th scope="col" className="border-0" style={{ width: '12%' }}>
                <div className="d-flex align-items-center compact-text">
                  Location
                </div>
              </th>
              <th scope="col" className="border-0" style={{ width: '12%' }}>
                <div className="d-flex align-items-center compact-text">
                  Industry
                </div>
              </th>
              <th scope="col" className="border-0" style={{ width: '12%' }}>
                <div 
                  className="d-flex align-items-center compact-text cursor-pointer"
                  onClick={() => handleSort('createdDate')}
                  style={{ cursor: 'pointer' }}
                  title={`Sort by ${sortField === 'createdDate' && sortDirection === 'desc' ? 'oldest first' : 'newest first'}`}
                >
                  Created
                  {sortField === 'createdDate' && (
                    <i className={`fas fa-sort-${sortDirection === 'desc' ? 'down' : 'up'} ml-1 text-primary`} />
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {(() => {
                             if (isLoading) {
                 return (
                   <tr>
                     <td colSpan="9" className="text-center py-5">
                      <div className="d-flex justify-content-center align-items-center">
                        <div className="spinner-border text-primary" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                        <span className="ml-3">Loading merchants...</span>
                      </div>
                    </td>
                  </tr>
                );
              }
              
                             if (error) {
                 return (
                   <tr>
                     <td colSpan="9" className="text-center py-5">
                      <div className="text-center">
                        <i className="ni ni-fat-remove text-danger" style={{ fontSize: '3rem' }}></i>
                        <h4 className="mt-3 text-danger">Error Loading Data</h4>
                        <p className="text-muted">{error}</p>
                        <Button color="primary" size="sm" onClick={() => window.location.reload()}>
                          <i className="ni ni-refresh mr-2"></i>
                          Try Again
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              }
              
              if (sortedMerchants.length === 0) {
                 return (
                   <tr>
                     <td colSpan="9" className="text-center py-5">
                       <div className="text-center">
                         <i className="ni ni-zoom-split-in text-muted" style={{ fontSize: '3rem' }}></i>
                         <h4 className="mt-3 text-muted">No matches found</h4>
                         <p className="text-muted">
                           No merchants match the current search criteria.
                         </p>
                       </div>
                     </td>
                   </tr>
                 );
               }
              
              return sortedMerchants.map((merchant) => (
                <tr key={merchant.id} className="border-bottom">
                  <td className="border-0">
                    <div className="d-flex align-items-center gap-1">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(merchant.id);
                        }}
                        className="btn-icon"
                        title="View Merchant Details"
                        style={{
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          padding: '0',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <i className="ni ni-single-copy-04" style={{ fontSize: '0.8rem' }} />
                      </Button>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openUpdateMerchant(merchant.id);
                        }}
                        className="btn-icon ml-1"
                        title="Update Merchant"
                        style={{
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          padding: '0',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <i className="ni ni-settings-gear-65" style={{ fontSize: '0.8rem' }} />
                      </Button>
                      {String(merchant.type || '').toUpperCase() !== 'MONITORING_CENTER' && (
                        <Button
                          color="primary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCreateApplication(merchant.id);
                          }}
                          className="btn-icon"
                          title="Create Application"
                          style={{
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            padding: '0',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <i className="ni ni-check-bold" style={{ fontSize: '0.8rem' }} />
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="border-0">
                    <div className="d-flex align-items-center">
                      <div className="compact-text text-truncate">
                        <div className="font-weight-bold text-dark mb-1">
                          {merchant.legalName || 'N/A'}
                        </div>
                        <small className="text-muted text-uppercase">
                          {merchant.type || 'DEALER'}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td className="border-0">
                    <div className="d-flex align-items-center">
                      <div className="compact-text text-truncate">
                        <div className="font-weight-bold text-dark mb-1">
                          {merchant.dbaName || 'N/A'}
                        </div>
                        <small className="text-muted">
                          Tax ID: {merchant.federalTaxId || 'N/A'}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td className="border-0">
                    <div className="d-flex align-items-center">
                      <div className="compact-text text-truncate">
                        <span className="font-weight-bold text-dark">
                          {merchant.stateOfInc || 'N/A'}
                        </span>

                      </div>
                    </div>
                  </td>
                  <td className="border-0">
                    <div className="d-flex align-items-center">
                      <div className="compact-text text-truncate">
                        <span className="font-weight-bold text-dark">
                          {merchant.industry || 'N/A'}
                        </span>
                        <small className="text-muted d-block">
                          {merchant.businessStructure || 'N/A'}
                        </small>
                      </div>
                    </div>
                  </td>
                   <td className="border-0">
                     <div className="d-flex align-items-center">
                       <div className="compact-text text-truncate">
                         <div className="font-weight-bold text-dark mb-1">
                           {formatDate(merchant.createdDate || merchant.createdAt || merchant.created_at)}
                         </div>
                         <small className="text-muted">
                           {merchant.createdBy || merchant.created_by || 'System'}
                         </small>
                       </div>
                     </div>
                   </td>
                </tr>
              ));
            })()}
          </tbody>
        </Table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="d-md-none">
        {(() => {
          if (isLoading) {
            return (
              <div className="text-center py-5">
                <div className="d-flex justify-content-center align-items-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <span className="ml-3">Loading merchants...</span>
                </div>
              </div>
            );
          }
          
          if (error) {
            return (
              <div className="text-center py-5">
                <i className="ni ni-fat-remove text-danger" style={{ fontSize: '3rem' }}></i>
                <h4 className="mt-3 text-danger">Error Loading Data</h4>
                <p className="text-muted">{error}</p>
                <Button color="primary" size="sm" onClick={() => window.location.reload()}>
                  <i className="ni ni-refresh mr-2"></i>
                  Try Again
                </Button>
              </div>
            );
          }
          
          if (sortedMerchants.length === 0) {
            return (
              <div className="text-center py-5">
                <i className="ni ni-zoom-split-in text-muted" style={{ fontSize: '3rem' }}></i>
                <h4 className="mt-3 text-muted">No matches found</h4>
                <p className="text-muted">
                  No merchants match the current search criteria.
                </p>
              </div>
            );
          }
          
          return (
            <div className="row">
              {sortedMerchants.map((merchant) => (
                <div key={merchant.id} className="col-12 mb-3">
                  <Card className="shadow-sm border-0">
                    <CardBody className="p-3">
                      {/* Header with Business Info */}
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center">
                          <div>
                            <h6 className="mb-1 font-weight-bold text-dark">
                              {merchant.legalName || 'N/A'}
                            </h6>
                            <div className="d-flex align-items-center mb-1">
                              <Badge color="info" className="badge mr-2 text-uppercase">
                                {merchant.type || 'DEALER'}
                              </Badge>
                              <small className="text-muted">
                                {merchant.industry || 'N/A'}
                              </small>
                            </div>
                            <small className="text-muted">
                              {merchant.stateOfInc || 'N/A'}
                            </small>
                          </div>
                        </div>
                        <div className="d-flex gap-1">
                          <Button
                            color="primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleView(merchant.id);
                            }}
                            className="btn-icon"
                            title="View Merchant Details"
                            style={{
                              borderRadius: '50%',
                              width: '32px',
                              height: '32px',
                              padding: '0',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <i className="ni ni-single-copy-04" style={{ fontSize: '0.8rem' }} />
                          </Button>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openUpdateMerchant(merchant.id);
                            }}
                            className="btn-icon ml-1"
                            title="Update Merchant"
                            style={{
                              borderRadius: '50%',
                              width: '32px',
                              height: '32px',
                              padding: '0',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <i className="ni ni-settings-gear-65" style={{ fontSize: '0.8rem' }} />
                          </Button>
                          {String(merchant.type || '').toUpperCase() !== 'MONITORING_CENTER' && (
                            <Button
                              color="primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openCreateApplication(merchant.id);
                              }}
                              className="btn-icon"
                              title="Create Application"
                              style={{
                                borderRadius: '50%',
                                width: '28px',
                                height: '28px',
                                padding: '0',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <i className="ni ni-check-bold" style={{ fontSize: '0.8rem' }} />
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* DBA & Tax ID */}
                      <div className="d-flex align-items-center mb-3">
                        <div>
                          <div className="font-weight-bold text-dark mb-1">
                            {merchant.dbaName || 'N/A'}
                          </div>
                          <small className="text-muted">
                            Tax ID: {merchant.federalTaxId || 'N/A'}
                          </small>
                        </div>
                      </div>

                      {/* Business Structure and Established Date */}
                      <div className="row mb-3">
                        <div className="col-6">
                          <div className="d-flex align-items-center">
                            <div>
                              <small className="text-muted d-block">Structure</small>
                              <span className="font-weight-bold text-dark">
                                {merchant.businessStructure || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div>
                            <small className="text-muted d-block">Established</small>
                            <small className="font-weight-bold text-dark">
                              {formatDate(merchant.dateEstablished)}
                            </small>
                          </div>
                        </div>
                      </div>

                      {/* Created Date and Created By */}
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <div>
                            <small className="text-muted d-block">Created</small>
                            <small className="font-weight-bold text-dark">
                              {formatDate(merchant.createdDate || merchant.createdAt || merchant.created_at)}
                            </small>
                          </div>
                        </div>
                        <div className="text-right">
                          <small className="text-muted d-block">Created By</small>
                          <small className="text-muted">
                            {merchant.createdBy || merchant.created_by || 'System'}
                          </small>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Pagination Controls */}
      {actualTotalElements > 0 && !isLoading && !error && (
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center px-3 py-2 border-top">
          <div className="text-muted compact-text mb-2 mb-sm-0">
            Showing {formatNumber(showingFrom)}-{formatNumber(showingTo)} of {formatNumber(actualTotalElements)}
          </div>
          <div className="d-flex align-items-center flex-wrap">
            <Button
              color="light"
              size="sm"
              onClick={() => handlePageChangeInternal(displayCurrentPage - 1)}
              disabled={displayCurrentPage === 1}
              className="mr-1"
              style={{ padding: '4px 8px', fontSize: '0.75rem' }}
            >
              <i className="fas fa-chevron-left" />
            </Button>
            
                         {/* Page numbers */}
             <div className="d-flex align-items-center">
               {(() => {
                 const pages = [];
                 const maxVisiblePages = 5;
                 
                 let startPage = Math.max(1, displayCurrentPage - Math.floor(maxVisiblePages / 2));
                 let endPage = Math.min(displayTotalPages, startPage + maxVisiblePages - 1);
                 
                 if (endPage - startPage + 1 < maxVisiblePages) {
                   startPage = Math.max(1, endPage - maxVisiblePages + 1);
                 }

                 for (let i = startPage; i <= endPage; i++) {
                   pages.push(i);
                 }

                 return pages.map(page => (
                   <Button
                     key={page}
                     color={page === displayCurrentPage ? "primary" : "light"}
                     size="sm"
                     onClick={() => handlePageChangeInternal(page)}
                     className="mx-1"
                     style={{ 
                       minWidth: '32px', 
                       padding: '4px 8px', 
                       fontSize: '0.75rem' 
                     }}
                   >
                     {page}
                   </Button>
                 ));
               })()}
             </div>
            
            <Button
              color="light"
              size="sm"
              onClick={() => handlePageChangeInternal(displayCurrentPage + 1)}
              disabled={displayCurrentPage === displayTotalPages}
              className="ml-1"
              style={{ padding: '4px 8px', fontSize: '0.75rem' }}
            >
              <i className="fas fa-chevron-right" />
            </Button>
          </div>
        </div>
      )}

       {/* Merchant Details Modal */}
       <MerchantDetailsModal
         isOpen={isModalOpen}
         toggle={toggleModal}
         merchant={selectedMerchant}
       />

       {/* Create Application Modal */}
       <CreateApplicationModal
         isOpen={isCreateAppOpen}
         toggle={closeCreateApplication}
         merchantId={selectedMerchant?.id}
         onCreated={() => {}}
       />

       {/* Update Merchant Modal */}
       <UpdateMerchantModal
         isOpen={isUpdateOpen}
         toggle={closeUpdateMerchant}
         merchantId={selectedMerchant?.id}
         onUpdated={() => {
           setIsUpdateOpen(false);
           try { onRefresh(); } catch (_) {}
         }}
       />
     </div>
     </>
   );
 }

export default MerchantTable;
